import { useEffect, useState } from "react";
import { finAPI, fmtINR, fmtINRshort, fmtDate, METHOD_STYLE, STATUS_STYLE } from "./finApi";
import CountUp from "@/pages/operations/CountUp";
import {
  TrendingUp, Wallet, Banknote, CreditCard, Smartphone, Undo2,
  Percent, ShieldAlert, Coins, CalendarClock, Moon, ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// ── Dark executive card
const HeroTile = ({ label, value, icon: Icon, sub, testid, accent = "text-hocp-gold" }) => (
  <div data-testid={testid}
    className="on-dark relative overflow-hidden bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-5 border border-white/10">
    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-hocp-gold/5 blur-2xl pointer-events-none"/>
    <div className="flex items-start justify-between relative">
      <div className={`w-10 h-10 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" strokeWidth={1.75}/>
      </div>
    </div>
    <div className="mt-4 relative">
      <div className="font-display text-[26px] font-semibold text-white tabular-nums leading-none">{value}</div>
      <div className="text-[11px] text-slate-200 font-medium mt-2 uppercase tracking-wider">{label}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
    </div>
  </div>
);

// ── Method split bar
const MethodBar = ({ data, total }) => {
  const entries = Object.entries(data || {});
  if (!entries.length) return <div className="text-[12px] text-slate-300 italic">No collections today.</div>;
  const t = Number(total) || entries.reduce((s, [, v]) => s + Number(v), 0);
  return (
    <div className="space-y-3">
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-800/60">
        {entries.map(([m, v]) => {
          const pct = t > 0 ? (Number(v) / t) * 100 : 0;
          const st = METHOD_STYLE[m] || { chip: "bg-slate-500" };
          const bg = st.chip.split(" ")[0].replace("100", "500");
          return <div key={m} className={bg} style={{ width: pct + "%" }} title={`${st.label || m}: ${fmtINR(v)}`}/>;
        })}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {entries.map(([m, v]) => {
          const st = METHOD_STYLE[m] || { chip: "bg-slate-500", label: m };
          const bg = st.chip.split(" ")[0].replace("100", "500");
          return (
            <div key={m} className="flex items-center gap-2 text-[12px]">
              <span className={`w-2.5 h-2.5 rounded-full ${bg}`}/>
              <span className="text-slate-200 flex-1">{st.label || m}</span>
              <span className="text-white font-medium tabular-nums">{fmtINRshort(v)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function FinanceDashboard() {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const load = () => finAPI.dashboard().then(setData).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const k = data?.kpis || {};
  const rev = k.todays_revenue || "0";
  const byMethod = k.by_method || {};

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await finAPI.scan();
      const load = await finAPI.dashboard();
      setData(load);
      alert(`Revenue audit complete. ${res.created_flags} new flag(s) created.`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  // Phase B placeholders — kept structural so backend can plug data later
  const adr = k.adr ?? null;
  const revpar = k.revpar ?? null;

  return (
    <div className="space-y-5" data-testid="fin-dashboard">
      {/* ── HERO STRIP ─────────────────────────────────────────────── */}
      <div className="on-dark relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1020] via-[#070B1F] to-[#0F1530] p-6 border border-white/10">
        <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-hocp-gold/10 blur-3xl pointer-events-none"/>
        <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-sky-500/10 blur-3xl pointer-events-none"/>
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-300 flex items-center gap-2 font-medium">
              <CalendarClock className="w-3.5 h-3.5"/> Business Date
              <span className="text-hocp-gold font-semibold tabular-nums" data-testid="fin-business-date">
                {data?.business_date || "—"}
              </span>
              {k.day_closed
                ? <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/25 text-emerald-200 text-[10px] font-semibold ring-1 ring-emerald-400/30">
                    <Moon className="w-3 h-3"/> DAY CLOSED
                  </span>
                : <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/25 text-amber-200 text-[10px] font-semibold ring-1 ring-amber-400/30">
                    <Moon className="w-3 h-3"/> DAY OPEN
                  </span>}
            </div>
            <div className="mt-3 font-display text-[42px] font-bold text-white tabular-nums leading-none" data-testid="fin-todays-revenue">
              {fmtINR(rev)}
            </div>
            <div className="text-[13px] text-slate-300 mt-2">
              Total collections received today — decimal-safe, event-ledger authoritative.
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={runScan} disabled={scanning} data-testid="fin-run-audit-scan"
              className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-[13px] font-semibold inline-flex items-center gap-2 transition-colors disabled:opacity-60 shadow-sm">
              <ShieldAlert className="w-4 h-4 text-hocp-gold"/>
              {scanning ? "Scanning…" : "Run Revenue Audit"}
            </button>
            <button onClick={() => nav("/finance-os/events")} data-testid="fin-open-ledger"
              className="px-4 py-2.5 rounded-xl bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-105 transition-all shadow-sm">
              Event Ledger <ArrowRight className="w-4 h-4"/>
            </button>
          </div>
        </div>
      </div>

      {/* ── DARK KPI STRIP ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <HeroTile label="Cash Collected"   value={fmtINRshort(byMethod.cash || 0)}
                    icon={Banknote}   testid="fin-kpi-cash"  accent="text-emerald-300"/>
        <HeroTile label="Card Collected"   value={fmtINRshort(byMethod.card || 0)}
                    icon={CreditCard} testid="fin-kpi-card"  accent="text-sky-300"/>
        <HeroTile label="UPI Collected"    value={fmtINRshort(byMethod.upi || 0)}
                    icon={Smartphone} testid="fin-kpi-upi"   accent="text-violet-300"/>
        <HeroTile label="Pending Refunds"  value={<CountUp value={Number(k.pending_refunds) || 0}/>}
                    icon={Undo2}      testid="fin-kpi-refunds" accent="text-amber-300"/>
        <HeroTile label="Pending Discounts" value={<CountUp value={Number(k.pending_discounts) || 0}/>}
                    icon={Percent}    testid="fin-kpi-discounts" accent="text-fuchsia-300"/>
        <HeroTile label="Revenue Flags"    value={<CountUp value={Number(k.revenue_flags) || 0}/>}
                    icon={ShieldAlert} testid="fin-kpi-flags"
                    accent={Number(k.revenue_flags) > 0 ? "text-rose-300" : "text-slate-400"}/>
      </div>

      {/* ── SECOND ROW ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Method Split */}
        <div className="on-dark lg:col-span-1 bg-gradient-to-br from-[#0F1530] to-[#0B1020] rounded-2xl p-5 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium">Collection Mix</div>
              <div className="text-white font-display text-[15px] font-semibold mt-1">Cash · Card · UPI</div>
            </div>
            <TrendingUp className="w-5 h-5 text-hocp-gold"/>
          </div>
          <MethodBar data={byMethod} total={rev}/>
        </div>

        {/* Operational tiles — light theme */}
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="fin-kpi-open-shifts">
            <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center mb-2.5">
              <Wallet className="w-4 h-4" strokeWidth={1.75}/>
            </div>
            <div className="font-display text-[22px] font-semibold text-hocp-navy tabular-nums leading-none">
              <CountUp value={Number(k.open_shifts) || 0}/>
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5 uppercase tracking-wider">Open Shifts</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="fin-kpi-pending-comps">
            <div className="w-8 h-8 rounded-lg bg-fuchsia-50 text-fuchsia-700 flex items-center justify-center mb-2.5">
              <Coins className="w-4 h-4" strokeWidth={1.75}/>
            </div>
            <div className="font-display text-[22px] font-semibold text-hocp-navy tabular-nums leading-none">
              <CountUp value={Number(k.pending_comps) || 0}/>
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5 uppercase tracking-wider">Pending Comps</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="fin-kpi-adr">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2.5">
              <TrendingUp className="w-4 h-4" strokeWidth={1.75}/>
            </div>
            <div className="font-display text-[22px] font-semibold text-hocp-navy tabular-nums leading-none">
              {adr != null ? fmtINRshort(adr) : "—"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5 uppercase tracking-wider">ADR</div>
            <div className="text-[10px] text-slate-400 mt-0.5">Wired in Phase B</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="fin-kpi-revpar">
            <div className="w-8 h-8 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center mb-2.5">
              <TrendingUp className="w-4 h-4" strokeWidth={1.75}/>
            </div>
            <div className="font-display text-[22px] font-semibold text-hocp-navy tabular-nums leading-none">
              {revpar != null ? fmtINRshort(revpar) : "—"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5 uppercase tracking-wider">RevPAR</div>
            <div className="text-[10px] text-slate-400 mt-0.5">Wired in Phase B</div>
          </div>

          {/* Recent payments panel */}
          <div className="col-span-2 md:col-span-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="font-display text-[15px] font-semibold text-hocp-navy">Latest Collections</h2>
                <p className="text-[12px] text-slate-500">Most recent 10 payments, most recent first.</p>
              </div>
              <button onClick={() => nav("/finance-os/payments")} data-testid="fin-dash-view-payments"
                className="text-[12px] text-hocp-navy font-medium hover:underline">View all →</button>
            </div>
            {!data?.recent_payments?.length ? (
              <div className="p-8 text-center text-[13px] text-slate-400" data-testid="fin-no-payments">No payments recorded yet.</div>
            ) : (
              <table className="w-full text-[13px]">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2.5">Code</th>
                    <th className="text-left px-4 py-2.5">Method</th>
                    <th className="text-left px-4 py-2.5">Guest</th>
                    <th className="text-right px-4 py-2.5">Amount</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-left px-4 py-2.5">At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.recent_payments.slice(0, 10).map(p => {
                    const m = METHOD_STYLE[p.method] || { chip: "bg-slate-100 text-slate-700", label: p.method };
                    const s = STATUS_STYLE[p.status] || { chip: "bg-slate-100 text-slate-700", label: p.status };
                    return (
                      <tr key={p.id} className="hover:bg-slate-50" data-testid={`fin-dash-pay-${p.code}`}>
                        <td className="px-4 py-2.5 font-mono text-[12px] text-hocp-navy">{p.code}</td>
                        <td className="px-4 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${m.chip}`}>{m.label}</span></td>
                        <td className="px-4 py-2.5 text-slate-700">{p.guest_name || <span className="text-slate-400 italic">—</span>}</td>
                        <td className="px-4 py-2.5 text-right font-medium tabular-nums text-hocp-navy">{fmtINR(p.amount)}</td>
                        <td className="px-4 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${s.chip}`}>{s.label}</span></td>
                        <td className="px-4 py-2.5 text-slate-500 text-[12px]">{fmtDate(p.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
